import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { useEffect } from 'react';
import {
  fallbackConversationTitle,
  isAgentGuidanceTitle,
  normalizeReportedConversationTitle,
} from '@/lib/conversations/conversation-title';

/** The trimmed text of the first user message, or undefined when there is none. */
export function firstUserMessageText(messages: readonly CrowdexUIMessage[]): string | undefined {
  return messages
    .find((message) => message.role === 'user')
    ?.parts.find((part) => part.type === 'text')
    ?.text.trim();
}

/**
 * Keeps the conversation title in sync with its first user message. Agent
 * sessions expose the agent display name, not an AI-generated conversation
 * title, so session metadata must never overwrite this value.
 */
export function useConversationTitleSync({
  preview,
  messages,
  agentName,
  sessionTitle,
  generatedTitle,
  setGeneratedTitle,
}: {
  preview: boolean;
  messages: readonly CrowdexUIMessage[];
  agentName?: string | null;
  sessionTitle?: string | null;
  generatedTitle: string;
  setGeneratedTitle: (title: string) => void;
}): void {
  useEffect(() => {
    const firstUserText = firstUserMessageText(messages);
    const fallbackTitle = firstUserText
      ? fallbackConversationTitle(firstUserText)
      : 'New conversation';
    const normalizedSessionTitle = sessionTitle?.trim();
    if (normalizedSessionTitle) {
      const reportedTitle = normalizeReportedConversationTitle(
        normalizedSessionTitle,
        fallbackTitle,
      );
      if (generatedTitle !== reportedTitle) setGeneratedTitle(reportedTitle);
      return;
    }
    if (
      preview ||
      (generatedTitle !== 'New conversation' &&
        !isAgentGuidanceTitle(generatedTitle) &&
        (agentName === null || agentName === undefined || generatedTitle !== agentName))
    )
      return;
    if (firstUserText) {
      setGeneratedTitle(fallbackTitle);
    }
  }, [agentName, generatedTitle, messages, preview, sessionTitle, setGeneratedTitle]);
}
